import { useEffect, useMemo } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { PickInfo } from '@deck.gl/core/lib/deck';
import { PickingInfo } from '@deck.gl/core/typed';
import { FeatureCollection, Geometry, centroid } from '@turf/turf';
import _, { isEmpty } from 'lodash';
import { LngLatLike } from 'mapbox-gl';

import { V1Alpha1Input } from '@src/clients/dexter/api';
import { useMapViewSelector } from '@src/contexts';
import { GEOSJON_COLOR_DEFAULT } from '@src/utils/constants/MAP_LAYERS';
import { h2R } from '@src/utils/hexToRgb';
import { V1Alpha1Output, getCogURLForOutput } from '@src/utils/workFlowUtils';

interface useRenderJobBounds {
  jobBounds: Geometry;
}

interface useRenderSingleRasterOutput {
  output: V1Alpha1Input & V1Alpha1Output;
  format: string;
  url?: string;
}

interface useRenderGeojsonOutput {
  id?: string;
  geojson: FeatureCollection;
  onclick?: (properties: { [key: string]: string }, index: number) => void;
  onhover?: (properties: { [key: string]: string }, index: number) => void;
  meta_key?: string;
  selectedID?: string;
  hoverID?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateTriggers?: Record<string, any>;
  getFillColor?: (d: { properties: { [key: string]: string } }) => number[];
  getHighlightColor?: (d: PickingInfo) => number[];
  getLineColor?: (d: { properties: { [key: string]: string } }) => number[];
  beforeId?: string;
}

interface useRenderMapLabels {
  geojson: FeatureCollection;
  meta_key_text: string;
  show: boolean;
  id?: string;
}

export const useRenderJobBounds = ({ jobBounds }: useRenderJobBounds) => {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  useEffect(() => {
    if (mapInstance) {
      const subs = mapInstance.disableMapDraw();
      mapInstance.uploadGeoJson(jobBounds);
      return () => {
        subs();
      };
    }
  }, [jobBounds, mapInstance]);

  return null;
};

export const useRenderSingleRasterOutput = ({
  output,
  format,
  url
}: useRenderSingleRasterOutput) => {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  useEffect(() => {
    if (mapInstance) {
      const titilerURL = url ? url : getCogURLForOutput(output, format);
      mapInstance.addTileLayer({
        args: [titilerURL, { id: titilerURL }],
        beforeId: 'gl-draw-polygon-fill.cold'
      });

      return () => {
        mapInstance.removeLayer(titilerURL);
      };
    }
  }, [output, mapInstance, format, url]);
  return null;
};

export const PolygonFill = h2R(GEOSJON_COLOR_DEFAULT) as {
  r: number;
  g: number;
  b: number;
};

export const useRenderFarmsGeojsonOutput = ({
  id = 'dexter-geojson-layer',
  geojson,
  onclick = () => {},
  onhover = () => {},
  meta_key,
  selectedID,
  hoverID,
  getFillColor,
  getLineColor,
  getHighlightColor,
  updateTriggers,
  beforeId
}: useRenderGeojsonOutput) => {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFeatureClick = (info: PickInfo<any>) => {
    const { object, index } = info;

    if (onclick) onclick(object, index);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFeatureHover = (info: PickInfo<any>) => {
    const { object, index } = info;

    if (onhover) onhover(object, index);
  };

  useEffect(() => {
    if (!mapInstance || _.isEmpty(geojson)) return;

    mapInstance.removeLayer(id);

    mapInstance.addGeoJSONLayer({
      data: geojson,
      options: { id },
      onClick: onFeatureClick,
      onHover: onFeatureHover,
      updateTriggers:
        selectedID || hoverID || updateTriggers
          ? {
              getFillColor: [selectedID, hoverID],
              ...(updateTriggers ?? {})
            }
          : undefined,
      getFillColor: getFillColor
        ? getFillColor
        : d => {
            if (
              selectedID &&
              meta_key &&
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              [selectedID, hoverID].includes(d.properties[meta_key])
            ) {
              return [PolygonFill.r, PolygonFill.g, PolygonFill.b, 100];
            }

            return [PolygonFill.r, PolygonFill.g, PolygonFill.b, 25];
          },
      getHighlightColor,
      getLineColor,
      beforeId,
      autoHighlight: true
    });
  }, [
    mapInstance,
    id,
    beforeId,
    geojson,
    meta_key,
    selectedID,
    hoverID,
    updateTriggers,
    getFillColor,
    getHighlightColor,
    getLineColor,
    onFeatureClick,
    onFeatureHover
  ]);
};

export const useRenderMapLabels = ({
  geojson,
  meta_key_text,
  show,
  id = 'dexter-label-layer'
}: useRenderMapLabels) => {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  const labels = useMemo(() => {
    if (_.isEmpty(geojson)) return [];

    return geojson.features
      .filter(i => i.geometry)
      .map(i => ({
        position: centroid(i).geometry.coordinates,
        text: `${i?.properties?.[meta_key_text] ?? ''}`
      }));
  }, [geojson, meta_key_text]);

  useEffect(() => {
    if (!mapInstance) return;

    mapInstance.removeLayer(id);

    if (!show || !labels.length) return;

    mapInstance.addTextLayer({
      data: labels,
      options: { id },
      getText: d => d.text,
      getPosition: d => d.position,
      zoom: mapInstance.getZoom()
    });
  }, [id, labels, mapInstance, show]);
};

export const useRenderSingleTextLabel = ({
  id = 'dexter-label-layer',
  show,
  label,
  beforeId
}: {
  id?: string;
  show: boolean;
  label: { text: string; position: LngLatLike } | null;
  beforeId?: string;
}) => {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  useEffect(() => {
    if (mapInstance && show && !isEmpty(label)) {
      const zoom = mapInstance.getZoom();

      mapInstance.removeLayer(id);

      mapInstance.addTextLayer({
        data: [label],
        options: { id },
        getText: d => d.text,
        getPosition: d => d.position,
        zoom,
        beforeId
      });
    }
  }, [beforeId, id, label, mapInstance, show]);
};
